import { getIsAiWeddingPlannerEnabled } from '@bridebook/web/lib/ab-tests/tests/global/LIVE-28398_AiWeddingPlanner';
import { isPlannerAvailableForCountry } from '@bridebook/web/lib/ai-planner/availability';
import type { WeddingWriter } from '@bridebook/web/lib/ai-planner/wedding-context-summary';
import { summariseProfile } from '@bridebook/web/lib/ai-planner/wedding-profile';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import type { IApplicationState } from '@bridebook/web/lib/types';
import { getProfile, getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { createSelector } from 'reselect';

/**
 * Whether the couple can have the planner at all, before their own choice.
 * The settings switch reads this, so it stays reachable after they turn it off.
 */
export const getIsAIPlannerEligible = (state: IApplicationState) =>
  // The default profile (initial and signed-out state) has no `l10n`, though a cast hides it
  // from the types. The page shell reads this, so a crash here blanks the whole app.
  isPlannerAvailableForCountry(state.weddings.profile.l10n?.country) &&
  // Control has no planner; the cohort is unresolved until the test loads.
  getIsAiWeddingPlannerEnabled(state);

// The couple turned the planner off in settings
export const getIsAIPlannerDisabledByCouple = (state: IApplicationState) =>
  state.aiPlanner.isDisabledByCouple;

// The single client-side check for showing the planner.
export const getIsAIPlannerAvailable = (state: IApplicationState) =>
  getIsAIPlannerEligible(state) && !getIsAIPlannerDisabledByCouple(state);

export const getAIPlannerUnreadReplies = (state: IApplicationState) =>
  state.aiPlanner.unreadReplies;

export const getIsAIPlannerPanelOpen = (state: IApplicationState) =>
  state.aiPlanner.isPanelOpen && getIsAIPlannerAvailable(state);

export const getIsAIPlannerKickoffPending = (state: IApplicationState) =>
  state.aiPlanner.isKickoffPending;

// The panel as the onboarding screen shows it: no header and no way to close it
export const getIsAIPlannerOnboarding = (state: IApplicationState) =>
  state.aiPlanner.isOnboardingActive && getIsAIPlannerPanelOpen(state);

export const getIsAIPlannerOnboardingExited = (state: IApplicationState) =>
  state.aiPlanner.onboardingExited;

/**
 * True only when the panel is showing in its docked desktop form, where it
 * takes 27% of the viewport and the page content is narrowed to the rest.
 * Below desktop the panel covers the screen instead, so nothing is reserved.
 */
export const getIsAIPlannerDocked = (state: IApplicationState) =>
  getIsAIPlannerPanelOpen(state) && getScreenType(state).isDesktop;

// The panel reopened from an earlier visit, shown without its opening animation
export const getIsAIPlannerPanelRestored = (state: IApplicationState) =>
  state.aiPlanner.isPanelRestored && getIsAIPlannerPanelOpen(state);

// The docked panel stretched over the whole screen, a desktop-only mode
export const getIsAIPlannerExpanded = (state: IApplicationState) =>
  state.aiPlanner.isPanelExpanded && getIsAIPlannerDocked(state);

/**
 * True where the panel covers the screen, which is everywhere but the desktop dock.
 * Another popup opening over it would be opening over the conversation.
 */
export const getIsAIPlannerBlocking = (state: IApplicationState) =>
  getIsAIPlannerPanelOpen(state) && !getScreenType(state).isDesktop;

/**
 * The couple's profile as the planner reads it, derived once per wedding change. The
 * summary sent to the route uses the same derivation, so client and model always agree.
 * Null until a wedding loads.
 */
export const selectPlannerProfile = createSelector(
  [getWeddingId, getProfile],
  (weddingId, wedding) => (weddingId ? summariseProfile(wedding) : null),
);

/**
 * Who is writing: the wedding's primary user, or a collaborator who need not be either
 * partner. Unknown until the wedding's users are loaded, and the extractor is told so.
 */
export const selectPlannerWriter = (state: IApplicationState): WeddingWriter | undefined => {
  const userId = state.users.user?.id;
  const me = state.weddings.collaborators.find((collaborator) => collaborator.id === userId);
  if (!me) return undefined;
  return me.primary ? 'owner' : 'collaborator';
};
